/**
 * The two artwork vocabularies either side of the product-pairing cutover.
 *
 * These are identities, not display aliases. An old Packaging Back asset is
 * still a truthful Packaging Back asset; the active catalogue decides what a
 * new order starts with, while evidence already on a record decides its
 * history.
 */
export const LEGACY_ARTWORK_COMPONENTS = [
  'Charm',
  'Bracelet Cord',
  'Packaging Front',
  'Packaging Back',
] as const;

export const PRODUCT_PAIRED_ARTWORK_COMPONENTS = [
  'Charm',
  'Bracelet Cord',
  'Packaging Front and Back',
  'Impact Card Front and Back',
] as const;

const LEGACY_ONLY = new Set<string>(['Packaging Front', 'Packaging Back']);
const PRODUCT_PAIRED_ONLY = new Set<string>([
  'Packaging Front and Back',
  'Impact Card Front and Back',
]);

function distinct(values: readonly string[]): string[] {
  return [...new Set(values)];
}

/** The exact component identities an order's assets say actually exist. */
export function artworkComponents(values: readonly string[]): string[] {
  const ids = distinct(values);
  const rank = new Map<string, number>();
  for (const id of [...LEGACY_ARTWORK_COMPONENTS, ...PRODUCT_PAIRED_ARTWORK_COMPONENTS]) {
    if (!rank.has(id)) rank.set(id, rank.size);
  }
  return ids.sort((left, right) => {
    const leftRank = rank.get(left) ?? Number.MAX_SAFE_INTEGER;
    const rightRank = rank.get(right) ?? Number.MAX_SAFE_INTEGER;
    return leftRank === rightRank ? left.localeCompare(right) : leftRank - rightRank;
  });
}

/**
 * A sample submission needs a complete required list, including slots with no
 * option yet. A packaging-specific option identifies its generation. Before
 * that evidence exists, the active catalogue is the only honest answer.
 */
export function submissionComponents(
  activeComponents: readonly string[],
  optionComponents: readonly string[],
): string[] {
  if (optionComponents.some((component) => LEGACY_ONLY.has(component))) {
    return [...LEGACY_ARTWORK_COMPONENTS];
  }
  if (optionComponents.some((component) => PRODUCT_PAIRED_ONLY.has(component))) {
    return [...PRODUCT_PAIRED_ARTWORK_COMPONENTS];
  }
  return distinct(activeComponents);
}
